import * as React from "react"
import { router } from "@inertiajs/react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

// ── shared types (the JSON the SellInDriver controller serializes) ────────────
export type Period = { year: number; month: number }
export type Ref = { source_kind: string; source_id: number | null; dataset: string; period: string }
export type PageMeta = { total_count: number; page: number; per_page: number }

export type Evidence = {
  summary?: string
  metrics?: Record<string, unknown>
  refs?: Ref[]
}

export type Attribution = {
  driver_type: string
  direction: string | null
  magnitude: number | null
  confidence: number | null
  rank: number
  label: "identified" | "unexplained"
  evidence: Evidence
}

// #51 M5 — where the product's movement localizes along PS→SS→SO that month.
export type CrossEchelon = {
  ps_flow_qty: number | null
  ss_flow_qty: number | null
  so_flow_qty: number | null
  ps_delta_pct: number | null
  ss_delta_pct: number | null
  so_delta_pct: number | null
  ps_ss_residual: number | null
  origin_tier: string | null
  pattern: string
  dq_flags: string[]
}

export type DriverRow = {
  id: number
  period: Period
  product: string
  party: string
  tier: string
  counterparty_code: string | null
  net_value: number | null
  prev_month_value: number | null
  last_year_value: number | null
  delta_mom_pct: number | null
  delta_yoy_pct: number | null
  volume_effect: number | null
  price_mix_effect: number | null
  stock_delta: number | null
  sell_out_qty: number | null
  sell_out_source: string | null
  sell_out_delta: number | null
  bridge_residual: number | null
  days_of_cover: number | null
  sell_through_pct: number | null
  service_level_pct: number | null
  price_gap_pct: number | null
  market_share_delta: number | null
  dq_flags: string[]
}

export type IndexRow = {
  id: number
  period: Period
  product: string
  party: string
  tier: string
  delta_mom_pct: number | null
  lead_driver: string | null
  lead_label: "identified" | "unexplained" | null
  driver_count: number
  origin_tier: string | null
  pattern: string | null
}

// ── driver_type → human label + tone (evidence, never causation) ──────────────
type Tone = React.ComponentProps<typeof Badge>["tone"]
const DRIVER_META: Record<string, { label: string; tone: Tone }> = {
  inventory_availability: { label: "Ketersediaan / stok", tone: "accent" },
  demand_shift: { label: "Pergeseran permintaan", tone: "accent" },
  price_change: { label: "Perubahan harga", tone: "accent" },
  promo_activity: { label: "Aktivitas promo", tone: "signal" },
  discount_cosmo: { label: "Diskon (Cosmo)", tone: "signal" },
  new_listing: { label: "Listing baru", tone: "signal" },
  price_gap: { label: "Gap harga", tone: "neutral" },
  competitor_share: { label: "Share kompetitor", tone: "neutral" },
  account_directive: { label: "Direktif akun", tone: "signal" },
  account_signal: { label: "Sinyal akun", tone: "neutral" },
  unexplained: { label: "Belum terjelaskan", tone: "muted" },
}
export function driverLabel(type: string | null): string {
  if (!type) return "—"
  return DRIVER_META[type]?.label ?? type
}
export function driverTone(type: string | null): Tone {
  if (!type) return "muted"
  return DRIVER_META[type]?.tone ?? "muted"
}
export function DriverBadge({ type }: { type: string | null }) {
  return <Badge tone={driverTone(type)}>{driverLabel(type)}</Badge>
}

export function LabelBadge({ label }: { label: "identified" | "unexplained" | null }) {
  if (label === "identified") return <Badge tone="success">Teridentifikasi</Badge>
  if (label === "unexplained") return <Badge tone="muted">Belum terjelaskan</Badge>
  return <Badge tone="muted">—</Badge>
}

// ── #51 M5: echelon (tier) + cross-echelon localization ───────────────────────
const TIER_META: Record<string, { short: string; label: string; flow: string }> = {
  SS: { short: "SS", label: "Secondary Sales", flow: "Distributor → Outlet" },
  PS: { short: "PS", label: "Primary Sales", flow: "Pabrik → Distributor" },
  SO: { short: "SO", label: "Sell Out", flow: "Outlet → Konsumen" },
}
export function tierLabel(tier: string | null): string {
  if (!tier) return "—"
  const meta = TIER_META[tier]
  return meta ? `${meta.short} · ${meta.flow}` : tier
}
// The party column header adapts to the echelon: SS keys by account, PS by SAP counterparty.
export function partyHeader(tier: string): string {
  return tier === "PS" ? "Counterparty (SAP)" : "Akun"
}
export function TierBadge({ tier }: { tier: string | null }) {
  if (!tier) return null
  const meta = TIER_META[tier]
  return <Badge tone={tier === "PS" ? "signal" : "accent"}>{meta?.short ?? tier}</Badge>
}

// Localization origin (the most-upstream materially-moving stage) — co-occurrence, not cause.
export function originLabel(tier: string | null): string {
  if (!tier) return "—"
  return { PS: "PS (pabrik/supply)", SS: "SS (akun/distributor)", SO: "SO (offtake konsumen)" }[tier] ?? tier
}

const PATTERN_META: Record<string, { label: string; tone: Tone }> = {
  ps_originated: { label: "Berasal hulu (PS)", tone: "signal" },
  ss_local: { label: "Lokal SS (akun)", tone: "accent" },
  so_demand: { label: "Hilir (offtake SO)", tone: "accent" },
  propagated: { label: "Menembus rantai", tone: "signal" },
  divergent: { label: "Divergen antar-eselon", tone: "neutral" },
  aligned_flat: { label: "Datar selaras", tone: "muted" },
}
export function patternLabel(pattern: string | null): string {
  if (!pattern) return "—"
  return PATTERN_META[pattern]?.label ?? pattern
}
// A compact origin badge for the Index — shown only when the move localized somewhere.
export function OriginBadge({ originTier, pattern }: { originTier: string | null; pattern: string | null }) {
  if (!originTier || !pattern || pattern === "aligned_flat") return null
  const tone = PATTERN_META[pattern]?.tone ?? "muted"
  return (
    <Badge tone={tone} title={`Terlokalisasi: ${patternLabel(pattern)}`}>
      ⛓ {originTier}
    </Badge>
  )
}

// The PS→SS→SO cross-echelon strip — three stage chips (flow qty + MoM %), the
// localized origin highlighted, the pattern named. Honest "tahap tak terukur" where a
// stage has no data for this product×month (the trans_dist/b2b populations are disjoint
// in parts of the data). CO-OCCURRENCE across independent stage measures, never cause.
export function CrossEchelonStrip({ ce }: { ce: CrossEchelon }) {
  const stages: { tier: "PS" | "SS" | "SO"; flow: number | null; delta: number | null }[] = [
    { tier: "PS", flow: ce.ps_flow_qty, delta: ce.ps_delta_pct },
    { tier: "SS", flow: ce.ss_flow_qty, delta: ce.ss_delta_pct },
    { tier: "SO", flow: ce.so_flow_qty, delta: ce.so_delta_pct },
  ]
  const localized = ce.origin_tier != null && ce.pattern !== "aligned_flat"
  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="mb-0 text-sm font-medium text-ink-display">Lokalisasi lintas-eselon (PS → SS → SO)</h2>
        {localized ? (
          <Badge tone={PATTERN_META[ce.pattern]?.tone ?? "muted"}>{patternLabel(ce.pattern)}</Badge>
        ) : (
          <Badge tone="muted">Datar selaras</Badge>
        )}
      </div>
      <p className="mt-1 text-sm text-ink-muted">
        {localized ? (
          <>
            Pergerakan terlokalisasi di <span className="font-medium text-ink-body">{originLabel(ce.origin_tier)}</span> —
            eselon paling hulu yang bergerak material. Ko-okurensi antar-tahap, bukan klaim sebab-akibat.
          </>
        ) : (
          <>Tidak ada tahap yang bergerak material pada produk ini — tak ada lokalisasi.</>
        )}
      </p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-3">
        {stages.map((stage, i) => (
          <React.Fragment key={stage.tier}>
            <div
              className={`flex-1 rounded-lg border p-4 ${
                ce.origin_tier === stage.tier ? "border-accent bg-accent/5" : "border-hairline bg-surface"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-display text-sm text-ink-display">{stage.tier}</span>
                {ce.origin_tier === stage.tier && <Badge tone="accent">Origin</Badge>}
              </div>
              <div className="mt-1 text-xs text-ink-muted">{TIER_META[stage.tier]?.flow}</div>
              {stage.flow == null ? (
                <div className="mt-2 text-xs text-ink-muted">Tahap tak terukur</div>
              ) : (
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="font-display text-lg text-ink-display tabular-nums">{fmtInt(stage.flow)}</span>
                  <Badge tone={deltaTone(stage.delta)}>{fmtSignedPct(stage.delta)}</Badge>
                </div>
              )}
            </div>
            {i < stages.length - 1 && (
              <div className="hidden items-center text-ink-muted sm:flex" aria-hidden="true">
                →
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
      {ce.dq_flags.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-2">
          {ce.dq_flags.map((flag, i) => (
            <li key={`${flag}-${i}`}>
              <Badge tone="muted">{flag}</Badge>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

// ── direction → arrow + word ──────────────────────────────────────────────────
export function directionLabel(direction: string | null): string {
  if (direction === "up") return "↑ naik"
  if (direction === "down") return "↓ turun"
  return "—"
}

// ── citation: every number traces to its source dataset(s) ────────────────────
export function SourceRefs({ refs }: { refs?: Ref[] }) {
  if (!refs || refs.length === 0) return null
  const seen = new Set<string>()
  const parts: string[] = []
  for (const r of refs) {
    const key = `${r.dataset}·${r.period ?? "—"}`
    if (!seen.has(key)) {
      seen.add(key)
      parts.push(key)
    }
  }
  if (parts.length === 0) return null
  return (
    <p className="mt-1 text-xs text-ink-muted" title={parts.join(" · ")}>
      Sumber: {parts.join(" · ")}
    </p>
  )
}

// ── number formatting ─────────────────────────────────────────────────────────
const isNum = (value: unknown): value is number => typeof value === "number" && !Number.isNaN(value)

export function fmtInt(value: number | null | undefined): string {
  if (!isNum(value)) return "—"
  return new Intl.NumberFormat("id-ID").format(Math.round(value))
}
export function fmtPct(value: number | null | undefined, digits = 1): string {
  if (!isNum(value)) return "—"
  return `${value.toFixed(digits)}%`
}
export function fmtSignedPct(value: number | null | undefined, digits = 1): string {
  if (!isNum(value)) return "—"
  const sign = value > 0 ? "+" : ""
  return `${sign}${value.toFixed(digits)}%`
}
export function fmtConfidence(value: number | null | undefined): string {
  if (!isNum(value)) return "—"
  return `${Math.round(value * 100)}%`
}
export function fmtIdr(value: number | null | undefined): string {
  if (!isNum(value)) return "—"
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(value)
}
export function periodLabel(p: Period): string {
  return `${p.year}-${String(p.month).padStart(2, "0")}`
}

// A signed-% delta gets a tone (up = success, down = danger, flat = muted).
export function deltaTone(value: number | null | undefined): Tone {
  if (!isNum(value)) return "muted"
  if (value >= 10) return "success"
  if (value <= -10) return "danger"
  return "muted"
}

// ── pagination (self-contained for this feature folder) ───────────────────────
export function Pagination({
  meta,
  path,
  query,
}: {
  meta: PageMeta
  path: string
  query: Record<string, string | number | undefined | null>
}) {
  const totalPages = Math.max(1, Math.ceil(meta.total_count / meta.per_page))
  if (totalPages <= 1) return null

  const go = (page: number) => {
    const q: Record<string, string> = {}
    Object.entries(query).forEach(([k, v]) => {
      if (v != null && v !== "") q[k] = String(v)
    })
    q.page = String(page)
    router.get(path, q, { preserveState: true, preserveScroll: true, replace: true })
  }

  return (
    <div className="mt-6 flex items-center justify-between text-sm text-ink-muted">
      <span>
        Halaman {meta.page} dari {totalPages} ({meta.total_count})
      </span>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" disabled={meta.page <= 1} onClick={() => go(meta.page - 1)}>
          Sebelumnya
        </Button>
        <Button variant="secondary" size="sm" disabled={meta.page >= totalPages} onClick={() => go(meta.page + 1)}>
          Berikutnya
        </Button>
      </div>
    </div>
  )
}

// ── M5: AI "why" narration (kill-switched) — mirrors #46's shared helpers ─────
export type NarrationStatus = "pending" | "narrating" | "draft" | "needs_review" | "published" | "failed"

export type NarrationSource = {
  dataset: string | null
  period: string | null
  source_kind: string | null
  source_id: number | null
}

export type Narration = {
  id: number
  status: NarrationStatus
  md_body: string | null
  model: string | null
  input_tokens: number
  output_tokens: number
  cost: number
  validation: { passed?: boolean; regenerated?: boolean; unknown_tokens?: string[] } | null
  error_message: string | null
  generated_at: string | null
  published_at: string | null
  period: Period
  sources: NarrationSource[]
}

// The narration is actively running in these states — the page polls while so.
export const NARRATION_ACTIVE: NarrationStatus[] = ["pending", "narrating"]

const NARRATION_STATUS_META: Record<NarrationStatus, { label: string; tone: Tone }> = {
  pending: { label: "Antre", tone: "muted" },
  narrating: { label: "Menarasikan", tone: "accent" },
  draft: { label: "Draf", tone: "accent" },
  needs_review: { label: "Perlu Tinjau", tone: "signal" },
  published: { label: "Terbit", tone: "success" },
  failed: { label: "Gagal", tone: "danger" },
}

export function NarrationStatusBadge({ status }: { status: NarrationStatus }) {
  const meta = NARRATION_STATUS_META[status] ?? NARRATION_STATUS_META.pending
  return <Badge tone={meta.tone}>{meta.label}</Badge>
}

const NUMBER_RE = /(\d[\d.,]*\d|\d)/

// Render the cited prose as paragraphs. On a needs_review narration, numbers the
// validator could not ground are highlighted amber so they can be corrected.
export function NarratedProse({ text, unknownTokens = [] }: { text: string; unknownTokens?: string[] }) {
  const paras = (text ?? "").trim().split(/\n{2,}/).filter(Boolean)
  if (paras.length === 0) return <p className="text-ink-muted">—</p>
  return (
    <div className="space-y-3 text-ink-body">
      {paras.map((para, i) => (
        <p key={i}>{annotateUnknown(para, unknownTokens)}</p>
      ))}
    </div>
  )
}

function annotateUnknown(text: string, unknownTokens: string[]) {
  if (unknownTokens.length === 0) return text
  return text.split(NUMBER_RE).map((part, i) =>
    i % 2 === 1 && unknownTokens.includes(part) ? (
      <span
        key={i}
        className="rounded bg-amber-100 px-0.5 font-medium text-amber-700 tabular-nums dark:bg-amber-900/40 dark:text-amber-300"
        title="Angka ini tidak dapat dicocokkan ke context pack — perlu ditinjau."
      >
        {part}
      </span>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  )
}

// Distinct "dataset · period" labels from a narration's grounded citations.
export function narrationSourceLabels(sources: NarrationSource[]): string[] {
  const seen = new Set<string>()
  const parts: string[] = []
  for (const s of sources) {
    const key = `${s.dataset ?? "—"}·${s.period ?? "—"}`
    if (!seen.has(key)) {
      seen.add(key)
      parts.push(key)
    }
  }
  return parts
}

export function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}
